import { allocatePayablePeriods, allocationTotal, payablePeriods } from "@chams-pay/application";
import { normalizeSchoolLevelLabel } from "@chams-pay/contracts";
import type {
  CaptureReadinessContract,
  LocalFinancialResultContract,
  Locale,
  ObligationContract,
  PaymentAllocationContract,
  PaymentMethodContract,
  StudentProfileContract,
} from "@chams-pay/contracts";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { localStore } from "./client.js";
import { deploymentId } from "./deployment.js";
import { casablancaDate } from "./format.js";
import { visibleStudentObligations } from "./student-obligation-visibility.js";

export type PaymentStep = "selection" | "allocation" | "details" | "confirmation" | "document";
export type PaymentMethodKind = PaymentMethodContract["method"];
export type PaymentCaptureError = "invalid" | "failure" | null;

/** The capture dialog always walks the same five steps, in this order. */
export const PAYMENT_STEPS = [
  "selection",
  "allocation",
  "details",
  "confirmation",
  "document",
] as const satisfies readonly PaymentStep[];

export type PaymentStepNumber = 1 | 2 | 3 | 4 | 5;

export class PaymentReconciliationInvariantError extends Error {
  readonly code = "PAYMENT_RECONCILIATION_INVARIANT";

  constructor() {
    super("Local payment allocations exceed the authoritative remaining obligation");
    this.name = "PaymentReconciliationInvariantError";
  }
}

export function remainingAfterAllocation(
  remainingMad: string,
  allocatedMad: string,
): string | null {
  if (!/^(?:0|[1-9][0-9]*)$/.test(allocatedMad)) return null;
  const remaining = BigInt(remainingMad);
  const allocated = BigInt(allocatedMad);
  return allocated <= remaining ? (remaining - allocated).toString() : null;
}

export interface PaymentDetailsDraft {
  readonly method: PaymentMethodKind;
  readonly receivedDate: string;
  readonly backdatingReason: string;
  readonly note: string;
  readonly bank: string;
  readonly chequeNumber: string;
  readonly drawer: string;
  readonly senderLabel: string;
  readonly transferReference: string;
  readonly statedDate: string;
}

export interface PaymentController {
  readonly step: PaymentStep;
  readonly stepNumber: PaymentStepNumber;
  readonly today: string;
  readonly obligations: readonly ObligationContract[];
  readonly periods: ReturnType<typeof payablePeriods>;
  readonly selectedPeriods: ReadonlySet<string>;
  readonly selectedPeriodEntries: ReturnType<typeof payablePeriods>;
  readonly allocations: readonly PaymentAllocationContract[];
  readonly allocated: string;
  readonly allocatedNumeric: bigint;
  readonly allocationsValid: boolean;
  readonly detailsValid: boolean;
  readonly busy: boolean;
  readonly error: PaymentCaptureError;
  readonly result: LocalFinancialResultContract | null;
  readonly receiptLocale: Locale;
  readonly draft: PaymentDetailsDraft;
  readonly blocked: boolean;
  readonly backdated: boolean;
  readonly canAdvance: boolean;
  readonly togglePeriod: (period: string) => void;
  readonly selectAllOverdue: () => void;
  readonly clearPeriods: () => void;
  readonly setAllocation: (obligationId: string, amountMad: string) => void;
  readonly patchDraft: (patch: Partial<PaymentDetailsDraft>) => void;
  readonly setReceiptLocale: (locale: Locale) => void;
  readonly goto: (step: PaymentStep) => void;
  readonly advance: () => void;
  readonly retreat: () => void;
  readonly finalize: () => Promise<void>;
  readonly restart: () => void;
}

/**
 * The payment capture machine. Amounts stay whole-MAD strings end to end and
 * finalization always goes through the encrypted local store, never the network.
 */
export function usePayment({
  student,
  actorUserId,
  trustedObservedAt,
  captureReadiness,
  finalizedPayments,
  calendarSnapshotId,
  onQueueChanged,
  onFinalized,
}: {
  readonly student: StudentProfileContract;
  readonly actorUserId: string | null;
  readonly trustedObservedAt: string;
  readonly captureReadiness: CaptureReadinessContract | null;
  readonly finalizedPayments: readonly LocalFinancialResultContract[];
  readonly calendarSnapshotId?: string;
  readonly onQueueChanged: () => void;
  readonly onFinalized: () => void;
}): PaymentController {
  const today = casablancaDate();
  const [step, setStep] = useState<PaymentStep>("selection");
  const [allocations, setAllocations] = useState<readonly PaymentAllocationContract[]>([]);
  const [selectedPeriods, setSelectedPeriods] = useState<ReadonlySet<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<PaymentCaptureError>(null);
  const [result, setResult] = useState<LocalFinancialResultContract | null>(null);
  const [receiptLocale, setReceiptLocale] = useState<Locale>("ar");
  const [draft, setDraft] = useState<PaymentDetailsDraft>({
    method: "cash",
    receivedDate: today,
    backdatingReason: "",
    note: "",
    bank: "",
    chequeNumber: "",
    drawer: "",
    senderLabel: "",
    transferReference: "",
    statedDate: today,
  });

  useEffect(() => {
    setStep("selection");
    setAllocations([]);
    setSelectedPeriods(new Set());
    setError(null);
    setResult(null);
  }, [calendarSnapshotId]);

  /**
   * Payments still eligible to synchronize consume the obligation locally.
   * Rejected evidence remains in history but cannot alter ledger authority.
   */
  const obligations = useMemo(() => {
    const locallySettled = new Map<string, bigint>();
    for (const entry of finalizedPayments) {
      if (
        entry.payment.settlementStatus !== "settled" ||
        entry.queueState === "synced" ||
        entry.queueState === "rejected"
      )
        continue;
      for (const allocation of entry.payment.allocations)
        locallySettled.set(
          allocation.obligationId,
          (locallySettled.get(allocation.obligationId) ?? 0n) + BigInt(allocation.amountMad),
        );
    }
    const reconciled = student.obligations
      .filter(
        (obligation) =>
          calendarSnapshotId === undefined || obligation.calendarSnapshotId === calendarSnapshotId,
      )
      .map((obligation) => {
        const local = locallySettled.get(obligation.id) ?? 0n;
        if (local > BigInt(obligation.remainingMad))
          throw new PaymentReconciliationInvariantError();
        return {
          ...obligation,
          settledMad: (BigInt(obligation.settledMad) + local).toString(),
          remainingMad: (BigInt(obligation.remainingMad) - local).toString(),
        };
      });
    return visibleStudentObligations(student, reconciled);
  }, [calendarSnapshotId, finalizedPayments, student]);

  const periods = useMemo(() => payablePeriods(obligations), [obligations]);
  const selectedPeriodEntries = useMemo(
    () => periods.filter((entry) => selectedPeriods.has(entry.period)),
    [periods, selectedPeriods],
  );

  const allocated = useMemo(() => {
    try {
      return allocationTotal(allocations);
    } catch {
      return "invalid";
    }
  }, [allocations]);

  const allocationsValid = useMemo(() => {
    if (!/^[1-9][0-9]*$/.test(allocated)) return false;
    return allocations.every((allocation) => {
      if (!/^(?:0|[1-9][0-9]*)$/.test(allocation.amountMad)) return false;
      const obligation = obligations.find((item) => item.id === allocation.obligationId);
      return (
        Boolean(obligation) && BigInt(allocation.amountMad) <= BigInt(obligation!.remainingMad)
      );
    });
  }, [allocated, allocations, obligations]);

  const detailsValid = useMemo(() => {
    if (draft.receivedDate > today || draft.receivedDate < student.calendar.activeFrom)
      return false;
    if (draft.receivedDate < today && !draft.backdatingReason.trim()) return false;
    if (draft.method === "cheque")
      return Boolean(draft.bank.trim() && draft.chequeNumber.trim() && draft.drawer.trim());
    if (draft.method === "transfer")
      return Boolean(
        draft.bank.trim() &&
        draft.transferReference.trim() &&
        draft.statedDate &&
        draft.statedDate <= today,
      );
    return true;
  }, [draft, student.calendar.activeFrom, today]);

  const togglePeriod = useCallback(
    (period: string) => {
      setSelectedPeriods((current) => {
        const next = new Set(current);
        if (next.has(period)) next.delete(period);
        else next.add(period);
        setAllocations(allocatePayablePeriods(obligations, next));
        return next;
      });
      setError(null);
    },
    [obligations],
  );

  const selectAllOverdue = useCallback(() => {
    const next = new Set(
      periods
        .filter((entry) => entry.overdue && entry.remainingMad !== "0")
        .map((entry) => entry.period),
    );
    setSelectedPeriods(next);
    setAllocations(allocatePayablePeriods(obligations, next));
    setError(null);
  }, [obligations, periods]);

  const clearPeriods = useCallback(() => {
    setSelectedPeriods(new Set());
    setAllocations([]);
    setError(null);
  }, []);

  const methodContract = useCallback((): PaymentMethodContract => {
    if (draft.method === "cash") return { method: "cash" };
    if (draft.method === "cheque")
      return {
        method: "cheque",
        bank: draft.bank,
        chequeNumber: draft.chequeNumber,
        drawer: draft.drawer,
      };
    return {
      method: "transfer",
      bank: draft.bank,
      reference: draft.transferReference,
      statedDate: draft.statedDate,
      ...(draft.senderLabel.trim() ? { senderLabel: draft.senderLabel } : {}),
    };
  }, [draft]);

  const finalize = useCallback(async () => {
    if (!actorUserId || !detailsValid || !allocationsValid) {
      setError("invalid");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const finalized = await localStore.finalizeFinancialPayment({
        student: {
          id: student.id,
          nameAr: student.nameAr,
          nameFr: student.nameFr,
          classLabel: normalizeSchoolLevelLabel(student.currentClass),
        },
        obligations,
        amountMad: allocated,
        receivedDate: draft.receivedDate,
        today,
        activeFrom: student.calendar.activeFrom,
        activeThrough: student.calendar.activeThrough,
        method: methodContract(),
        allocations: allocations.filter((allocation) => allocation.amountMad !== "0"),
        actorUserId,
        deploymentId,
        trustedObservedAt,
        readinessNow: Date.now(),
        readinessMonotonicNow: performance.now(),
        ...(draft.backdatingReason.trim() ? { backdatingReason: draft.backdatingReason } : {}),
        ...(draft.note.trim() ? { note: draft.note } : {}),
      });
      setResult(finalized);
      onQueueChanged();
      onFinalized();
      setStep("document");
    } catch {
      setError("failure");
    } finally {
      setBusy(false);
    }
  }, [
    actorUserId,
    allocated,
    allocations,
    allocationsValid,
    detailsValid,
    draft,
    methodContract,
    obligations,
    onFinalized,
    onQueueChanged,
    student,
    today,
    trustedObservedAt,
  ]);

  const stepNumber = (PAYMENT_STEPS.indexOf(step) + 1) as PaymentStepNumber;
  const canAdvance =
    step === "selection"
      ? selectedPeriods.size > 0 && allocationsValid
      : step === "allocation"
        ? allocationsValid
        : step === "details"
          ? detailsValid
          : step === "confirmation"
            ? !busy && detailsValid && allocationsValid
            : true;

  return {
    step,
    stepNumber,
    today,
    obligations,
    periods,
    selectedPeriods,
    selectedPeriodEntries,
    allocations,
    allocated,
    allocatedNumeric: BigInt(allocated === "invalid" ? "0" : allocated),
    allocationsValid,
    detailsValid,
    busy,
    error,
    result,
    receiptLocale,
    draft,
    blocked: captureReadiness?.allowed === false,
    backdated: draft.receivedDate < today,
    canAdvance,
    togglePeriod,
    selectAllOverdue,
    clearPeriods,
    setAllocation: useCallback(
      (obligationId: string, amountMad: string) =>
        setAllocations((current) =>
          current.map((item) =>
            item.obligationId === obligationId ? { ...item, amountMad } : item,
          ),
        ),
      [],
    ),
    patchDraft: useCallback(
      (patch: Partial<PaymentDetailsDraft>) => setDraft((current) => ({ ...current, ...patch })),
      [],
    ),
    setReceiptLocale,
    goto: setStep,
    advance: useCallback(() => {
      setStep((current) => {
        const next = PAYMENT_STEPS[PAYMENT_STEPS.indexOf(current) + 1];
        return next ?? current;
      });
    }, []),
    retreat: useCallback(() => {
      setStep((current) => {
        const index = PAYMENT_STEPS.indexOf(current);
        return index > 0 ? PAYMENT_STEPS[index - 1]! : current;
      });
    }, []),
    finalize,
    restart: useCallback(() => {
      setSelectedPeriods(new Set());
      setAllocations([]);
      setResult(null);
      setStep("selection");
    }, []),
  };
}

/**
 * Presentation only: the running total counts up to its new value over one
 * short interval so the footer figure never jumps. Reduced-motion users get
 * the final value immediately.
 */
export function useCountUp(target: bigint, durationMs = 180): bigint {
  const [value, setValue] = useState(target);
  const from = useRef(target);
  useEffect(() => {
    const start = from.current;
    if (start === target) return;
    const reduced =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      from.current = target;
      setValue(target);
      return;
    }
    const startedAt = performance.now();
    let frame = 0;
    const tick = () => {
      const progress = Math.min(1, (performance.now() - startedAt) / durationMs);
      const eased = 1 - (1 - progress) * (1 - progress);
      const current = start + BigInt(Math.round(Number(target - start) * eased));
      setValue(progress === 1 ? target : current);
      if (progress < 1) frame = requestAnimationFrame(tick);
      else from.current = target;
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [durationMs, target]);
  return value;
}
